<template>
  <div class="modal-container">
    <div class="modal-content" v-if="!loading">
      <div class="mb-3">
        <div class="header-section">
          <div class="has-text-weight-semibold is-size-6 mb-5">
            {{ getTypeDisplayName(selectedType) }} zone - {{ currentRegion || "" }}
          </div>
          <div class="type-selector">
            <div class="field">
              <div class="control">
                <div class="select is-small">
                  <select v-model="selectedType">
                    <option v-for="type in availableShapeTypes" :key="type.value" :value="type.value">
                      {{ type.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="has-text-danger has-text-weight-semibold is-size-7 mb-2">
          {{ msg }}
        </div>

        <!-- Geofence Section -->
        <div v-if="selectedType === 'geofence'">
          <!-- Distribution Hub Selector -->
          <div class="field mb-2 mt-3">
            <div class="field has-addons">
              <div class="control is-expanded">
                <GlobalSelector v-model="selectedHub" label="Distribution Zone" type="single" dataSource="hubs"
                  placeholder="Select a distribution zone..." :disabled="false" />
              </div>
            </div>
          </div>

          <!-- Days of Service Section -->
          <div class="field mt-3 days-of-service">
            <label class="label is-size-7 has-text-weight-semibold mb-2">Days of Service</label>
            <div class="days-container">
              <label class="checkbox" v-for="(day, key) in daysOfService" :key="key">
                <input type="checkbox" v-model="daysOfService[key]" class="mr-1" />
                {{ getDayLabel(key) }}
              </label>
            </div>
          </div>

          <!-- Color Picker Section -->
          <div class="field mt-3">
            <label class="label is-size-7 has-text-weight-semibold mb-2">Shape Color</label>
            <div class="color-input-container">
              <div class="color-picker-wrapper">
                <input type="color" v-model="selectedColor" class="color-picker" />
              </div>
            </div>
          </div>
        </div>

        <!-- Billable Section -->
        <div v-if="selectedType === 'billable'">
          <div class="field mb-2">
            <div class="field has-addons">
              <div class="control is-expanded">
                <GlobalSelector v-model="zone" label="Zone" type="single" dataSource="zones"
                  placeholder="Select a zone..." :disabled="false" zoneType="billable" />
              </div>
            </div>
          </div>

          <div class="is-flex is-flex-direction-column is-flex-wrap-wrap is-align-items-baseline mt-1 mb-1"
            v-if="zone?.value">
            <GlobalSelector v-model="selectedClients" label="Clients" type="multi" dataSource="clients"
              placeholder="Select clients..." :disabled="true" />
          </div>
        </div>

        <!-- Payable Section -->
        <div v-if="selectedType === 'payable'">
          <div class="field mb-2">
            <div class="field has-addons">
              <div class="control is-expanded">
                <GlobalSelector v-model="zone" label="Zone" type="single" dataSource="zones"
                  placeholder="Select a zone..." :disabled="false" zoneType="payable" />
              </div>
            </div>
          </div>

          <div class="is-flex is-flex-direction-column is-flex-wrap-wrap is-align-items-baseline mt-1 mb-1"
            v-if="zone?.value">
            <GlobalSelector v-model="selectedContractors" label="Contractors" type="multi" dataSource="contractors"
              placeholder="Select contractors..." :disabled="true" />
          </div>
        </div>
      </div>

      <!-- Buttons container -->
      <div class="buttons-container">
        <div class="buttons is-centered">
          <button class="button is-small" @click="cancelEdit()">Cancel</button>
          <button class="button is-small is-success" @click="saveToFirestore">Save</button>
        </div>
      </div>
    </div>
    <div class="loader-container" v-else>
      <PulseLoader />
    </div>
  </div>
</template>
<script>
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import store from '../store'
import EventHandler from '../EventHandler'
import GlobalSelector from './GlobalSelector.vue'

export default {
  components: {
    PulseLoader,
    GlobalSelector
  },
  data() {
    return {
      msg: '',
      zone: null,
      selectedHub: null,
      loading: false,
      environment: store.state.environment,
      zoneProps: {
        color: '',
      },
      selectedClients: [], // Array of selected client IDs
      selectedContractors: [], // Array of selected contractor IDs
      selectedType: 'geofence', // Default to geofence
      selectedColor: '#ff0000', // Default color for geofence shapes
      daysOfService: {
        mon: false,
        tue: false,
        wed: false,
        thu: false,
        fri: false,
        sat: false,
        sun: false,
      },
    }
  },
  props: {
    region: {
      type: String
    },
    shapeType: {
      type: String
    },
    shapesToAdd: {
      type: [Array, Object]
    },
    /**
     * Selected shape type filter from Map.vue
     * If 'ALL_TYPES', allow all types, otherwise limit to the selected type
     */
    selectedShapeTypeFilter: {
      type: String,
      default: 'ALL_TYPES'
    }
  },
  watch: {
    /**
     * Watch for zone changes and update selected clients/contractors
     */
    'zone.zoneNameId': {
      handler(newZoneId, oldZoneId) {
        if (newZoneId && newZoneId !== oldZoneId) {
          // Get zone data and update selected entities based on zone type
          const zone = store.state.zoneInformations?.find(z => z.id === newZoneId);
          if (zone) {
            if (zone.type === 'payable') {
              // For contractors, just pass the IDs directly - GlobalSelector will handle the display
              this.selectedContractors = zone.clients || [];
              this.selectedClients = [];
            } else {
              this.selectedClients = zone.clients || [];
              this.selectedContractors = [];
            }
          }
        } else if (!newZoneId) {
          // Clear entities when zone is cleared
          this.selectedClients = [];
          this.selectedContractors = [];
        }
      },
      immediate: false
    },
    /**
     * Watch for selectedShapeTypeFilter changes and update selectedType accordingly
     */
    selectedShapeTypeFilter: {
      handler(newFilter) {
        if (newFilter !== 'ALL_TYPES') {
          // Set the selectedType to match the filter
          this.selectedType = newFilter;
        }
      },
      immediate: true
    }
  },
  computed: {
    currentRegion() {
      const currentRegionSelected = store.state.region;
      const selectedRegion = store.state.regions.find(
        (region) => region.id === currentRegionSelected
      );
      return selectedRegion?.name;
    },
    /**
     * Get available vendors from the store
     */
    availableVendors() {
      return store?.state?.vendors || [];
    },
    /**
     * Get available shape types based on the filter
     * If filter is 'ALL_TYPES', show all options, otherwise limit to the selected type
     */
    availableShapeTypes() {
      if (this.selectedShapeTypeFilter === 'ALL_TYPES') {
        return [
          { value: 'geofence', label: 'Geofence' },
          { value: 'billable', label: 'Billable' },
          { value: 'payable', label: 'Payable' }
        ];
      } else {
        // Map the filter value to the corresponding option
        const typeMap = {
          geofence: { value: 'geofence', label: 'Geofence' },
          billable: { value: 'billable', label: 'Billable' },
          payable: { value: 'payable', label: 'Payable' }
        };
        return typeMap[this.selectedShapeTypeFilter] ? [typeMap[this.selectedShapeTypeFilter]] : [];
      }
    }
  },
  methods: {
    getTypeDisplayName(type) {
      switch (type) {
        case 'geofence': return 'Geofence';
        case 'billable': return 'Billable';
        case 'payable': return 'Payable';
        default: return type;
      }
    },
    closeModal() {
      EventHandler.$emit('closeModal')
      this.close()
    },
    cancelEdit() {
      this.close()
    },
    async saveCoordinates(shapesToAdd, zoneId, shapeType, distributionZoneId, zoneClients, daysOfServiceData) {
      await this.updateMap({
        shapesToAdd,
        zoneId,
        shapeType,
        distributionZoneId,
        zoneClients,
        selectedType: this.selectedType,
        daysOfService: daysOfServiceData,
        color: this.selectedType === 'geofence' ? this.selectedColor : null
      })
      await this.removeShape()
    },
    async saveToFirestore() {
      this.loading = true
      const zone = this.zone?.value ? this.zone.value.trim() : ''
      const zoneId = this.zone?.zoneNameId ? this.zone.zoneNameId.trim() : ''
      const shapesToAdd = this.shapesToAdd
      const shapeType = this.shapeType.trim()
      const distributionZoneId = this.selectedHub?.DistributionZoneId || null

      // Determine which entities to pass based on type
      let zoneEntities = [];
      if (this.selectedType === 'billable') {
        zoneEntities = this.selectedClients;
      } else if (this.selectedType === 'payable') {
        // selectedContractors are now simple IDs, no need to extract
        zoneEntities = this.selectedContractors;
      }

      // Get days of service data for geofence type
      const daysOfServiceData = this.getDaysOfServiceForAPI();

      // Validate that at least one of zone or distribution zone is selected
      if ((!zone || !zoneId) && !distributionZoneId) {
        this.loading = false
        this.msg = 'Please select at least one zone or distribution zone'
        return
      }

      try {
        await this.saveCoordinates(shapesToAdd, zoneId, shapeType, distributionZoneId, zoneEntities, daysOfServiceData)
        this.loading = false
        EventHandler.$emit('closeModal')
        this.close()
        this.msg = ''
      } catch (err) {
        console.log('ERR: ', err)
        this.loading = false
        this.msg = 'Error adding'
      }
    },
    getDayLabel(key) {
      switch (key) {
        case 'mon':
          return 'Monday';
        case 'tue':
          return 'Tuesday';
        case 'wed':
          return 'Wednesday';
        case 'thu':
          return 'Thursday';
        case 'fri':
          return 'Friday';
        case 'sat':
          return 'Saturday';
        case 'sun':
          return 'Sunday';
        default:
          return key;
      }
    },
    getDaysOfServiceForAPI() {
      return {
        mon: this.daysOfService.mon || false,
        tue: this.daysOfService.tue || false,
        wed: this.daysOfService.wed || false,
        thu: this.daysOfService.thu || false,
        fri: this.daysOfService.fri || false,
        sat: this.daysOfService.sat || false,
        sun: this.daysOfService.sun || false,
      };
    }
  }
}
</script>
<style>
/* Target only our specific AddFSAZoneModal popup with the class we'll add */
.add-fsa-zone-modal-popup .leaflet-popup-content-wrapper {
  padding: 20px !important;
  border-radius: 4px !important;
  width: 450px !important;
}

.add-fsa-zone-modal-popup .leaflet-popup-content {
  width: auto !important;
  max-width: 450px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.add-fsa-zone-modal-popup .leaflet-popup-tip {
  width: 10px !important;
  height: 10px !important;
  margin: -5px auto 0 !important;
}

.add-fsa-zone-modal-popup .leaflet-popup-content>* {
  margin: 0 !important;
  padding: 0 !important;
}

.add-fsa-zone-modal-popup .leaflet-popup-close-button {
  display: none !important;
}
</style>

<style lang="scss" scoped>
@import "~leaflet/dist/leaflet.css";

/* Modal container for reduced width */
.modal-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
}

.modal-content {
  width: 100%;
  max-width: 450px;
  padding: 8px !important;
  display: flex;
  flex-direction: column;
  min-height: 300px;

  >div:first-child {
    flex-grow: 1;
  }
}

/* Button container to push buttons to bottom */
.buttons-container {
  margin-top: auto;
  padding-top: 1rem;
}

.loader-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 0;
  margin: 0;
}

.custom .leaflet-popup-tip,
.custom .leaflet-popup-content-wrapper {
  background: #e93434;
  color: #ffffff;
}

/* Button styling */
.buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  justify-content: center;

  .button {
    margin: 0 !important;
    flex: 0 0 auto;
    min-width: 80px;
    height: 28px !important;
    font-size: 0.75rem !important;
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
  }
}

.is-disabled {
  opacity: 0.65;
  cursor: not-allowed;

  :deep(.multiselect__tags) {
    background: #f5f5f5;
  }
}

.label.is-size-7 {
  color: #363636;
  font-weight: 600;
}

.field {
  margin-bottom: 0.75rem;
}

.field.has-addons {
  display: flex;

  .control {
    &.is-expanded {
      flex-grow: 1;
      flex-shrink: 1;
    }
  }
}

/* Color picker styling */
.color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker::-webkit-color-swatch {
  border: none;
}

.color-input-container {
  align-items: center;
}

.color-picker-wrapper {
  width: 36px;
  height: 30px;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  overflow: hidden;
  margin-right: 8px;
}

.color-picker {
  width: 200%;
  height: 200%;
  transform: translate(-25%, -25%);
  cursor: pointer;
}

/* Clients display styling */
.clients-display {
  max-height: 60px;
  overflow-y: auto;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.2;
  padding: 2px;
  border-radius: 3px;
  font-size: 0.7rem;
  min-height: 28px;
  display: flex;
  align-items: center;
  width: 400px;
  /* Fixed width */
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
}

/* Header section styling */
.header-section {
  display: flex !important;
  justify-content: space-between !important;
  align-items: flex-start !important;
  margin-bottom: 1rem !important;
}

.type-selector {
  flex-shrink: 0 !important;
}

.type-selector .buttons {
  margin: 0 !important;
}

.type-selector .button {
  font-size: 0.7rem !important;
  padding: 0.25rem 0.5rem !important;
  height: auto !important;
  line-height: 1.2 !important;
}

/* Days of Service styling */
.days-of-service .days-container {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 1rem !important;
  align-items: center !important;
}

.days-of-service .checkbox {
  display: flex !important;
  align-items: center !important;
  font-size: 0.75rem !important;
  margin: 0 !important;
  padding: 0.25rem 0 !important;
  white-space: nowrap !important;
}

.days-of-service .checkbox input[type="checkbox"] {
  margin-right: 0.5rem !important;
  margin-bottom: 0 !important;
  accent-color: #000000 !important;
  width: 14px !important;
  height: 14px !important;
}

.days-of-service .label {
  margin-bottom: 0.5rem !important;
}
</style>
